/**
 * 🎮 播放控制器
 *
 * 统一管理数组和WebSocket模式的播放控制
 * 支持播放、暂停、快进、进度控制等功能
 */

import { ArrayDataManager } from './ArrayDataManager';
import { WebSocketManager } from './WebSocketManager';
import type { VehicleData } from './RSMDataAdapter';

// === 播放状态定义 ===

/**
 * 播放状态
 */
export interface PlaybackState {
  /** 是否正在播放 */
  isPlaying: boolean;
  /** 播放进度 (0-1) */
  progress: number;
  /** 播放速度倍数 */
  speed: number;
  /** 总时长（秒） */
  duration: number;
  /** 当前时间（秒） */
  currentTime: number;
  /** 播放模式 */
  mode: 'array' | 'websocket' | 'idle';
  /** 是否循环播放 */
  loop: boolean;
  /** 是否暂停缓存 (WebSocket模式) */
  isPaused?: boolean;
  /** 缓存消息数量 (WebSocket模式) */
  bufferSize?: number;
}

/**
 * 播放控制配置
 */
export interface PlaybackControlConfig {
  /** 默认播放速度 */
  defaultSpeed?: number;
  /** 播放速度范围 */
  speedRange?: {
    min: number;
    max: number;
  };
  /** 是否启用循环播放 */
  enableLoop?: boolean;
  /** 更新间隔（毫秒） */
  updateInterval?: number;
}

// === 事件类型定义 ===

/**
 * 播放控制器事件
 */
export interface PlaybackControllerEvents {
  /** 播放状态变化 */
  'playback:stateChange': (state: PlaybackState) => void;
  /** 播放开始 */
  'playback:play': () => void;
  /** 播放暂停 */
  'playback:pause': () => void;
  /** 播放停止 */
  'playback:stop': () => void;
  /** 播放速度变化 */
  'playback:speedChange': (speed: number) => void;
  /** 播放进度变化 */
  'playback:progressChange': (progress: number, currentTime: number) => void;
  /** 播放结束 */
  'playback:ended': () => void;
  /** 车辆数据更新 */
  'data:update': (vehicles: VehicleData[], frameIndex?: number) => void;
  /** 播放错误 */
  'playback:error': (error: Error) => void;
}

// === 播放控制器类 ===

/**
 * 播放控制器
 */
export class PlaybackController {
  private arrayDataManager: ArrayDataManager | null = null;
  private webSocketManager: WebSocketManager | null = null;
  private config: Required<PlaybackControlConfig>;

  // 播放状态
  private state: PlaybackState = {
    isPlaying: false,
    progress: 0,
    speed: 1,
    duration: 0,
    currentTime: 0,
    mode: 'idle',
    loop: false,
  };

  // 播放控制
  private playbackTimer: ReturnType<typeof setInterval> | null = null;

  // 事件监听
  private eventListeners: Map<string, Set<Function>> = new Map();

  constructor(config: PlaybackControlConfig = {}) {
    // 默认配置
    this.config = {
      defaultSpeed: config.defaultSpeed ?? 1.0,
      speedRange: config.speedRange ?? { min: 0.1, max: 4.0 },
      enableLoop: config.enableLoop ?? true,
      updateInterval: config.updateInterval ?? 100,
    };

    this.state.speed = this.config.defaultSpeed;
    this.state.loop = this.config.enableLoop;

    this.initializeEventListeners();
  }

  /**
   * 初始化事件监听器
   */
  private initializeEventListeners(): void {
    const events: Array<keyof PlaybackControllerEvents> = [
      'playback:stateChange',
      'playback:play',
      'playback:pause',
      'playback:stop',
      'playback:speedChange',
      'playback:progressChange',
      'playback:ended',
      'data:update',
      'playback:error',
    ];

    events.forEach(event => {
      this.eventListeners.set(event, new Set());
    });
  }

  /**
   * 设置数组数据管理器
   */
  setArrayDataManager(arrayDataManager: ArrayDataManager): void {
    this.cleanup();

    this.arrayDataManager = arrayDataManager;
    this.webSocketManager = null;

    // 更新状态
    this.state.mode = 'array';
    this.state.duration = arrayDataManager.getDuration();
    this.state.currentTime = arrayDataManager.getCurrentTime();
    this.state.progress = arrayDataManager.getProgress();

    // 绑定事件
    this.bindArrayManagerEvents();

    this.emitStateChange();
  }

  /**
   * 设置WebSocket数据管理器
   */
  setWebSocketManager(webSocketManager: WebSocketManager): void {
    this.cleanup();

    this.webSocketManager = webSocketManager;
    this.arrayDataManager = null;

    // 更新状态
    this.state.mode = 'websocket';
    this.state.duration = 0; // WebSocket模式没有固定时长
    this.state.currentTime = 0;
    this.state.progress = 0;
    this.state.isPaused = false;
    this.state.bufferSize = 0;

    // 绑定事件
    this.bindWebSocketManagerEvents();

    this.emitStateChange();
  }

  /**
   * 绑定数组管理器事件
   */
  private bindArrayManagerEvents(): void {
    if (!this.arrayDataManager) return;

    this.arrayDataManager.on('frame:change', (_frame, vehicles, frameIndex) => {
      this.state.currentTime = this.arrayDataManager!.getCurrentTime();
      this.state.progress = this.arrayDataManager!.getProgress();

      this.emit('data:update', vehicles, frameIndex);
      this.emit(
        'playback:progressChange',
        this.state.progress,
        this.state.currentTime
      );

      this.emitStateChange();
    });

    this.arrayDataManager.on('data:error', error => {
      this.emit('playback:error', error);
    });
  }

  /**
   * 绑定WebSocket管理器事件
   */
  private bindWebSocketManagerEvents(): void {
    if (!this.webSocketManager) return;

    this.webSocketManager.on('message:rsm', (_rsmMessage, vehicles) => {
      this.emit('data:update', vehicles);
    });

    this.webSocketManager.on('buffer:statusChange', (isPaused, bufferSize) => {
      this.state.isPaused = isPaused;
      this.state.bufferSize = bufferSize;
      this.emitStateChange();
    });

    this.webSocketManager.on('connection:error', error => {
      this.emit('playback:error', error);
    });

    this.webSocketManager.on('message:error', error => {
      this.emit('playback:error', error);
    });
  }

  /**
   * 开始播放
   */
  play(): void {
    if (this.state.mode === 'idle') {
      console.warn('[PlaybackController] 未设置数据源，无法播放');
      return;
    }

    if (this.state.isPlaying) {
      console.warn('[PlaybackController] 已经在播放中');
      return;
    }

    this.state.isPlaying = true;

    if (this.state.mode === 'array') {
      this.startArrayPlayback();
    } else if (this.state.mode === 'websocket') {
      this.startWebSocketPlayback();
    }

    this.emit('playback:play');
    this.emitStateChange();
  }

  /**
   * 暂停播放
   */
  pause(): void {
    if (!this.state.isPlaying) {
      console.warn('[PlaybackController] 当前未在播放');
      return;
    }

    this.state.isPlaying = false;

    if (this.playbackTimer) {
      clearInterval(this.playbackTimer);
      this.playbackTimer = null;
    }

    if (this.state.mode === 'websocket' && this.webSocketManager) {
      this.webSocketManager.pause();
    }

    this.emit('playback:pause');
    this.emitStateChange();
  }

  /**
   * 停止播放
   */
  stop(): void {
    this.state.isPlaying = false;

    if (this.playbackTimer) {
      clearInterval(this.playbackTimer);
      this.playbackTimer = null;
    }

    if (this.state.mode === 'array' && this.arrayDataManager) {
      this.arrayDataManager.setCurrentFrameIndex(0);
      this.state.currentTime = 0;
      this.state.progress = 0;
    } else if (this.state.mode === 'websocket' && this.webSocketManager) {
      this.webSocketManager.clearBuffer();
      this.state.isPaused = false;
      this.state.bufferSize = 0;
    }

    this.emit('playback:stop');
    this.emitStateChange();
  }

  /**
   * 设置播放速度
   */
  setSpeed(speed: number): void {
    // 限制速度范围
    speed = Math.max(
      this.config.speedRange.min,
      Math.min(this.config.speedRange.max, speed)
    );

    if (speed === this.state.speed) {
      return;
    }

    this.state.speed = speed;

    // 如果正在播放，重新启动播放器以应用新速度
    if (this.state.isPlaying && this.state.mode === 'array') {
      this.startArrayPlayback();
    }

    this.emit('playback:speedChange', speed);
    this.emitStateChange();
  }

  /**
   * 跳转到指定进度（0-1）
   */
  seekTo(progress: number): void {
    progress = Math.max(0, Math.min(1, progress));

    if (this.state.mode === 'array' && this.arrayDataManager) {
      const success = this.arrayDataManager.seekToProgress(progress);
      if (success) {
        this.state.progress = progress;
        this.state.currentTime = this.arrayDataManager.getCurrentTime();
        this.emit(
          'playback:progressChange',
          this.state.progress,
          this.state.currentTime
        );
        this.emitStateChange();
      }
    } else if (this.state.mode === 'websocket') {
      console.warn('[PlaybackController] WebSocket模式不支持进度跳转');
    }
  }

  /**
   * 跳转到指定时间
   */
  seekToTime(timeInSeconds: number): void {
    if (this.state.mode === 'array' && this.arrayDataManager) {
      const success = this.arrayDataManager.seekToTime(timeInSeconds);
      if (success) {
        this.state.currentTime = timeInSeconds;
        this.state.progress = this.arrayDataManager.getProgress();
        this.emit(
          'playback:progressChange',
          this.state.progress,
          this.state.currentTime
        );
        this.emitStateChange();
      }
    } else if (this.state.mode === 'websocket') {
      console.warn('[PlaybackController] WebSocket模式不支持时间跳转');
    }
  }

  /**
   * 设置循环播放
   */
  setLoop(loop: boolean): void {
    this.state.loop = loop;
    this.emitStateChange();
  }

  /**
   * 下一帧（仅数组模式）
   */
  nextFrame(): boolean {
    if (this.state.mode === 'array' && this.arrayDataManager) {
      return this.arrayDataManager.nextFrame();
    }
    return false;
  }

  /**
   * 上一帧（仅数组模式）
   */
  previousFrame(): boolean {
    if (this.state.mode === 'array' && this.arrayDataManager) {
      return this.arrayDataManager.previousFrame();
    }
    return false;
  }

  /**
   * 恢复播放（WebSocket模式）
   */
  resume(): void {
    if (this.state.mode === 'websocket' && this.webSocketManager) {
      this.webSocketManager.resume();
    } else if (this.state.mode === 'array') {
      this.play();
    }
  }

  /**
   * 跳转到最新数据（WebSocket模式）
   */
  jumpToLatest(): void {
    if (this.state.mode === 'websocket' && this.webSocketManager) {
      this.webSocketManager.jumpToLatest();
    }
  }

  /**
   * 获取播放状态
   */
  getPlaybackState(): PlaybackState {
    return { ...this.state };
  }

  /**
   * 检查是否正在播放
   */
  isPlaying(): boolean {
    return this.state.isPlaying;
  }

  /**
   * 获取当前进度
   */
  getProgress(): number {
    return this.state.progress;
  }

  /**
   * 获取播放速度
   */
  getSpeed(): number {
    return this.state.speed;
  }

  /**
   * 获取总时长
   */
  getDuration(): number {
    return this.state.duration;
  }

  /**
   * 获取当前时间
   */
  getCurrentTime(): number {
    return this.state.currentTime;
  }

  /**
   * 获取播放模式
   */
  getMode(): 'array' | 'websocket' | 'idle' {
    return this.state.mode;
  }

  /**
   * 启动数组模式播放
   */
  private startArrayPlayback(): void {
    if (this.playbackTimer) {
      clearInterval(this.playbackTimer);
    }

    const frameRate = this.arrayDataManager?.getFrameRate() || 10;
    const interval = 1000 / frameRate / this.state.speed;

    this.playbackTimer = setInterval(() => {
      if (!this.state.isPlaying || !this.arrayDataManager) {
        return;
      }

      const hasNext = this.arrayDataManager.nextFrame();

      if (!hasNext) {
        // 播放结束
        if (this.state.loop) {
          // 循环播放，回到开始
          this.arrayDataManager.setCurrentFrameIndex(0);
        } else {
          // 停止播放
          this.pause();
          this.emit('playback:ended');
        }
      }
    }, interval);
  }

  /**
   * 启动WebSocket模式播放
   */
  private startWebSocketPlayback(): void {
    if (this.webSocketManager && this.state.isPaused) {
      this.webSocketManager.resume();
    }
    // WebSocket模式的播放主要依赖于数据流，不需要定时器
  }

  /**
   * 触发状态变化事件
   */
  private emitStateChange(): void {
    this.emit('playback:stateChange', this.getPlaybackState());
  }

  /**
   * 事件监听
   */
  on<K extends keyof PlaybackControllerEvents>(
    event: K,
    handler: PlaybackControllerEvents[K]
  ): void {
    const handlers = this.eventListeners.get(event);
    if (handlers) {
      handlers.add(handler);
    }
  }

  /**
   * 移除事件监听
   */
  off<K extends keyof PlaybackControllerEvents>(
    event: K,
    handler: PlaybackControllerEvents[K]
  ): void {
    const handlers = this.eventListeners.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * 触发事件
   */
  private emit<K extends keyof PlaybackControllerEvents>(
    event: K,
    ...args: Parameters<PlaybackControllerEvents[K]>
  ): void {
    const handlers = this.eventListeners.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          (handler as any)(...args);
        } catch (error) {
          console.error(
            `[PlaybackController] 事件处理器错误 (${event}):`,
            error
          );
        }
      });
    }
  }

  /**
   * 清理资源
   */
  private cleanup(): void {
    if (this.playbackTimer) {
      clearInterval(this.playbackTimer);
      this.playbackTimer = null;
    }

    this.state.isPlaying = false;
  }

  /**
   * 销毁控制器
   */
  dispose(): void {
    this.cleanup();
    this.eventListeners.clear();
    this.arrayDataManager = null;
    this.webSocketManager = null;
  }

  /**
   * 重置到初始状态
   */
  reset(): void {
    this.stop();
    this.setSpeed(this.config.defaultSpeed);
    this.setLoop(this.config.enableLoop);
  }
}

// === 导出工厂函数 ===

/**
 * 创建播放控制器
 */
export function createPlaybackController(
  config: PlaybackControlConfig = {}
): PlaybackController {
  return new PlaybackController(config);
}

// === 默认导出 ===

export default PlaybackController;
